import {CheckboxChangeEvent} from 'antd/es/checkbox';
import {useForm} from 'antd/es/form/Form';
import {isUndefined} from 'lodash-es';
import {
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {convertGroupingDataMapToArray} from 'components/table-report/utils/table-report-utils';
import {convertArrayToMap, convertMapToArray} from 'modules/data/utils/data.utils';
import {setFieldsValueUntouched, showMessage} from 'shared/utils';
import {useAppDispatch} from 'store/config/hooks';
import {IReportDdReferenceRule} from 'store/slices/report-configuration-slice/dd-references-configuration';
import {updateTotalRow} from 'store/slices/report-configuration-slice/dd-references-configuration/dd-references-configuration-api';
import {
    IReportConfigurationDataDto,
    IReportConfigurationReportColumnDto,
    IReportConfigurationReportGroupDto,
} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {reportConfigurationActions} from 'store/slices/report-configuration-slice/report-configuration-slice';
import {
    TReportConfigurationConvertedField,
} from 'store/slices/report-configuration-slice/report-configuration-types';
import {convertConfigurationDataFromResponse} from 'store/slices/report-configuration-slice/report-configuration/report-configuration-utils';
import {getTableAttributes} from 'store/slices/report-configuration-slice/report-dependencies';
import {fetchTableReportTemplateConfig} from 'store/slices/table-report-slice/table-report-slice-api';

import {TTableAttributesCustomSelectEntry} from '../../report-table-settings';
import {removeKeyNamesPostfix, renameDuplicateKeyNames} from '../../utils';
import {DEFAULT_SETTINGS, GROUPING_SETTINGS_PREFIX} from './dd-grouping-settings-modal-opener-constants';
import {convertAttributes} from './dd-grouping-settings-modal-opener-utils';

interface IUseDDGroupingSettingsModal {
    disabled?: boolean;
    drilldownReferenceRules: IReportDdReferenceRule[];
    index: number;
}

/**
 * @param params Объект с параметрами для настройки группировок:
 * @param params.drilldownReferenceRules - Массив правил, используется для получения правила по индексу `index`.
 * @param params.index - Индекс выбранного правила в массиве `drilldownReferenceRules`.
 * @returns Объект со значениями, необходимыми для управления модальным окном настроек группировок:
 *
 * - **usingGroup** - Флаг, указывающий, используется ли группировка.
 * - **isSavingData** - Показывает, что в данный момент идёт процесс сохранения данных.
 * - **isAttributesLoaded** - Показывает, что атрибуты таблицы были загружены с сервера.
 * - **form** - Экземпляр формы Ant Design для управления данными формы настроек группировок.
 * - **modalOpenerRef** - Ссылка на компонент для открытия модального окна (ModalOpenerComponentRef).
 * - **initialValues** - Начальные значения для инициализации формы с настройками группировок.
 * - **attributesEntries** - Список доступных атрибутов, полученных с сервера.
 * - **checkedFields** - Массив индексов выбранных полей в настройках группировок.
 * - **templateCode** - Код шаблона дочернего отчёта (drill-down), по которому загружаются атрибуты.
 * - **groupAfterUserFilterValue** - Флаг, указывающий, применяется ли группировка после пользовательских фильтров.
 * - **extendedAttributesEntries** - Расширенный список атрибутов, включающий дополнительный пункт "Общий итог".
 * - **fetchTableAttributesData** - Функция для загрузки и конвертации атрибутов таблицы.
 * - **handleFinish** - Обработчик сохранения настроек группировки при отправке формы.
 * - **setUsingGroup** - Сеттер для переключения флага `usingGroup`.
 * - **setCheckedFields** - Сеттер для обновления списка выбранных полей.
 * - **setGroupAfterUserFilterValue** - Сеттер для флага применения группировки после пользовательских фильтров.
 * - **handleCheck** - Обработчик изменения состояния чекбокса для полей в настройках группировок.
 */

export const useDDGroupingSettingsModalOpener = ({
    drilldownReferenceRules,
    index,
}: IUseDDGroupingSettingsModal) => {
    const dispatch = useAppDispatch();
    const {updateDdReferenceRulesFilter} = reportConfigurationActions;

    const drilldownReferenceRule = drilldownReferenceRules[index];
    const {totalRowDto} = drilldownReferenceRule;

    const [form] = useForm();

    const [tableAttributes, setTableAttributes] = useState<TTableAttributesCustomSelectEntry[]>([]);
    const [isAttributesLoaded, setIsAttributesLoaded] = useState(false);
    const [isSavingData, setIsSavingData] = useState(false);
    const [checkedFields, setCheckedFields] = useState<number[]>([]);

    const modalOpenerRef = useRef<IModalOpenerComponentRef>(null);

    const initialSettings = useMemo(() => {
        if (!totalRowDto) return DEFAULT_SETTINGS;

        return {
            reportGroups: convertGroupingDataMapToArray(
            totalRowDto?.reportGroups as {[key: string]: IReportConfigurationReportGroupDto},
            ),
            groupAfterUserFilter: totalRowDto.groupAfterUserFilter ?? DEFAULT_SETTINGS.groupAfterUserFilter,
            usingGroup: totalRowDto.usingGroup ?? DEFAULT_SETTINGS.usingGroup,
        };
    }, [totalRowDto]);

    const [initialValues, setInitialValues] = useState<any>({
        [GROUPING_SETTINGS_PREFIX]: totalRowDto?.[GROUPING_SETTINGS_PREFIX] || [],
    });
    const [
        groupAfterUserFilterValue,
        setGroupAfterUserFilterValue,
    ] = useState<boolean>(initialSettings?.groupAfterUserFilter);
    const [
        usingGroup,
        setUsingGroup,
    ] = useState<boolean>(Boolean(initialSettings?.usingGroup));

    const fetchTableAttributesData = async () => {
        if (isAttributesLoaded) {
            return;
        }

        if (!drilldownReferenceRule?.childReportTemplateCode) {
            setTableAttributes([]);
            return;
        }

        try {
            const attributesResponse = await getTableAttributes({
                templateCode: drilldownReferenceRule.childReportTemplateCode,
            });
            const configResponse = await fetchTableReportTemplateConfig({
                templateCode: drilldownReferenceRule.childReportTemplateCode,
                params: {},
                isExtended: false,
            });

            const {reportColumns} = convertConfigurationDataFromResponse({
                dataToConvert: configResponse?.data as Partial<IReportConfigurationDataDto>,
            });

            const convertedAttributes = convertAttributes(
                attributesResponse?.data,
                 reportColumns as TReportConfigurationConvertedField<IReportConfigurationReportColumnDto>,
            );
            setTableAttributes(convertedAttributes || []);
            setIsAttributesLoaded(true);
        } catch (error) {
            console.error('Ошибка при загрузке атрибутов:', error);
        }
    };

    const extendedAttributesEntries = useMemo(
        () => (tableAttributes
            ? [{label: 'Общий итог', value: 'GRAND_TOTAL'}, ...tableAttributes]
            : undefined),
        [tableAttributes],
    );

    const handleCheck = (e: CheckboxChangeEvent) => {
        if (e.target.checked) {
            setCheckedFields(prev => [...prev, e.target.value]);
        } else {
            setCheckedFields(prev => prev?.filter(val => val !== e.target.value));
        }
    };

    useEffect(() => {
        const rawReportGroups = convertMapToArray(
            totalRowDto?.[GROUPING_SETTINGS_PREFIX] || initialSettings.reportGroups,
        );

        const reportGroups = removeKeyNamesPostfix(
          rawReportGroups as TReportConfigurationConvertedField<IReportConfigurationReportGroupDto>,
        );

        if (reportGroups) {
            setInitialValues({
                reportGroups,
            });
            if (!isUndefined(initialSettings.groupAfterUserFilter)) {
                setGroupAfterUserFilterValue(initialSettings.groupAfterUserFilter);
            }
        }
    }, [initialSettings, totalRowDto]);

    useEffect(() => {
        if (initialValues) {
            setFieldsValueUntouched(form, initialValues);
        }
    }, [initialValues, form]);

    const handleFinish = async (values: any) => {
        const rawReportGroups = values?.[GROUPING_SETTINGS_PREFIX];
        const renamedReportGroups = renameDuplicateKeyNames(rawReportGroups);

        try {
            setIsSavingData(true);
            await updateTotalRow({
                data: {
                    totalRowDto: {
                        groupAfterUserFilter: groupAfterUserFilterValue,
                        usingGroup,
                        [GROUPING_SETTINGS_PREFIX]: convertArrayToMap<any>(renamedReportGroups),
                    },
                },
                id: drilldownReferenceRule?.id,
            });

            modalOpenerRef.current?.setIsModalOpen(false);
            dispatch(updateDdReferenceRulesFilter({}));
        } catch (error) {
            showMessage({
                message: 'Произошла ошибка при обновлении настроек итоговой строки',
                isError: true,
            });
            console.error('Произошла ошибка при обновлении настроек итоговой строки', error);
        } finally {
            setIsSavingData(false);
        }
    };

    return {
        usingGroup,
        isSavingData,
        isAttributesLoaded,
        form,
        modalOpenerRef,
        initialValues,
        attributesEntries: tableAttributes,
        checkedFields,
        templateCode: drilldownReferenceRule?.childReportTemplateCode,
        groupAfterUserFilterValue,
        extendedAttributesEntries,
        fetchTableAttributesData,
        handleFinish,
        setUsingGroup,
        setCheckedFields,
        setGroupAfterUserFilterValue,
        handleCheck,
    };
};
